import { PageHero } from "../components/PageHero";
import { Section, SectionHead, Card, Chip } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { BrowserFrame } from "../components/BrowserFrame";
import { CTASection } from "../sections/CTASection";
import { PILLARS, ENGINEERING_LAYERS } from "../data/capabilities";
import { productBySlug } from "../data/products";

/**
 * /software-development
 *
 * The brief was explicit that this must not read as staff augmentation,
 * and the way a page accidentally does that is by describing people
 * ("skilled engineers, flexible teams") instead of systems. So this page
 * describes systems: what gets built, what "production-grade" concretely
 * means here, and the two systems we shipped and now operate as the
 * evidence. Nothing on it advertises rates, headcount or availability.
 */

const ENGAGEMENTS = [
  {
    title: "MVP to production",
    body: "An idea with a real user waiting for it. We architect for the version that survives, ship the smallest thing that proves it, and avoid the two or three decisions that would force a rewrite at scale.",
  },
  {
    title: "Platform build",
    body: "Multi-tenant SaaS with the hard parts taken seriously from the first commit: isolation, roles, audit, billing boundaries, and data residency where the market requires it.",
  },
  {
    title: "AI into an existing product",
    body: "You have a working system and a place a model would genuinely help. We integrate it behind an interface you can evaluate, cost-cap and turn off, rather than scattering calls through your codebase.",
  },
  {
    title: "Hardening what already exists",
    body: "Something works but does not hold. We find what breaks under real load and real data, fix it in priority order, and leave the observability that shows it stayed fixed.",
  },
];

const STANDARDS = [
  {
    t: "Tests where they earn their keep",
    b: "Around the data model, the money, and the rules that would be expensive to get wrong. Not a coverage number chased for its own sake.",
  },
  {
    t: "Observability from the first commit",
    b: "Logs, traces and the handful of metrics that reveal a problem before a customer reports it. Added afterwards, it never quite works.",
  },
  {
    t: "Migrations that run forwards",
    b: "Schema changes that deploy without downtime and can be reasoned about a year later, on a database that already has real data in it.",
  },
  {
    t: "Isolation you can prove",
    b: "In a multi-tenant system, one company seeing another's data is not a bug, it is the end of the product. It gets designed in, not asserted.",
  },
  {
    t: "Costs with a ceiling",
    b: "Cloud and model spend that is projected before launch and capped in the code, so a traffic spike is an operations event rather than an invoice.",
  },
  {
    t: "A handover that works",
    b: "Documented decisions, runnable locally, deployable by someone who was not there. We would rather you could leave than have to stay.",
  },
];

export function SoftwareDevelopment() {
  const pillar = PILLARS[1];
  const hazri = productBySlug("hazri-book");

  return (
    <>
      <PageHero
        eyebrow="Software Development"
        title={
          <>
            From architecture{" "}
            <span className="text-gradient">to production.</span>
          </>
        }
        lede="We take complex ideas and turn them into systems carrying real users, real money and real regulatory weight. We are not a body shop, we do not rent out developers by the month, and we do not compete on rate."
        primary={{ href: "/contact", label: "Scope a build" }}
        secondary={{ href: "/work", label: "See what we shipped" }}
        aside={
          <Card className="p-6 sm:p-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
              What we build
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {pillar.capabilities.map((c) => (
                <Chip key={c}>{c}</Chip>
              ))}
            </div>
            <div className="mt-6 border-t border-white/[0.08] pt-5">
              <p className="text-sm leading-relaxed text-white/60">
                The measure of a build is not whether it demos. It is whether
                it is still standing in month fourteen, with data in it and
                someone else maintaining it.
              </p>
            </div>
          </Card>
        }
      />

      <Section id="engagements">
        <div className="container">
          <SectionHead
            eyebrow="Engagements"
            title={
              <>
                Four shapes,{" "}
                <span className="text-gradient">and we will tell you which one you are.</span>
              </>
            }
            lede="Most requests arrive described as one of these and turn out to be another. Naming it correctly at the start is worth more than any estimate."
          />

          <div className="mt-10 grid gap-4 sm:mt-14 sm:grid-cols-2">
            {ENGAGEMENTS.map((e, i) => (
              <Reveal key={e.title} delay={(i % 2) * 0.07}>
                <Card interactive accent={i % 2 ? "violet" : "emerald"} className="h-full p-6 sm:p-7">
                  <h3 className="font-display text-lg font-semibold tracking-tight">
                    {e.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/62">{e.body}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section id="standards" tight>
        <div className="container">
          <SectionHead
            eyebrow="What production-grade means here"
            title={
              <>
                Six things we do{" "}
                <span className="text-gradient">whether or not you ask.</span>
              </>
            }
            lede="These are not upsells and they are not phase two. They are the difference between software that works on the day it ships and software that works a year later."
          />

          <div className="mt-10 grid overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] sm:grid-cols-2 lg:grid-cols-3">
            {STANDARDS.map((s, i) => (
              <Reveal
                key={s.t}
                delay={(i % 3) * 0.06}
                className="border-b border-r border-white/[0.08] p-6 last:border-b-0 sm:p-7"
              >
                <h3 className="font-display text-base font-semibold tracking-tight text-white">
                  {s.t}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-white/60">{s.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section id="stack">
        <div className="container">
          <SectionHead
            eyebrow="The stack, by layer"
            title={
              <>
                We think in architectures,{" "}
                <span className="text-gradient">not framework names.</span>
              </>
            }
          />
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {ENGINEERING_LAYERS.map((layer, i) => (
              <Reveal key={layer.layer} delay={(i % 2) * 0.06}>
                <Card className="h-full p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-base font-semibold tracking-tight">
                      {layer.layer}
                    </h3>
                    <span className="text-[11px] text-white/35">{layer.note}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {layer.items.map((item) => (
                      <Chip key={item}>{item}</Chip>
                    ))}
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {hazri?.shots ? (
        <Section id="evidence" tight>
          <div className="container">
            <SectionHead
              eyebrow="Evidence"
              title={
                <>
                  This is ours.{" "}
                  <span className="text-gradient">Designed, built, and still run by us.</span>
                </>
              }
              lede="Hazri Book is in production with a contracting company in Saudi Arabia. Offline-first capture, multi-tenant isolation, Arabic throughout, and a month-end close that has to survive a payroll dispute."
            />
            <Reveal delay={0.1} className="mt-10">
              <div className="grid gap-4 sm:grid-cols-2">
                {hazri.shots.map((s, i) => (
                  <BrowserFrame
                    key={s.src}
                    src={s.src}
                    alt={s.alt}
                    label={`Hazri Book · ${s.caption}`}
                    priority={i === 0}
                  />
                ))}
              </div>
            </Reveal>
          </div>
        </Section>
      ) : null}

      <CTASection
        title={
          <>
            Have something{" "}
            <span className="text-gradient">that needs building?</span>
          </>
        }
        lede="Send us the problem rather than the specification. The specification is usually where the interesting mistakes are hiding, and we would rather find them before you have paid for them."
        primary={{ href: "/contact", label: "Scope a build" }}
        secondary={{ href: "/consulting", label: "Start with strategy instead" }}
      />
    </>
  );
}
